import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Gauge, Plus, Edit, Trash2, Search, MapPin, Box, RefreshCw, Zap, Flame, Droplet, Car, Sun, Wind, Snowflake, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const PAGE_SIZE = 100;

const Meters = () => {
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState(null);
  const [meters, setMeters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [meterStats, setMeterStats] = useState({ total: 0, by_commodity: {} });
  const sentinelRef = useRef(null);
  const searchTimerRef = useRef(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // CSV Upload states
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    location_id: '',
    space_id: '',
    meter_number: '',
    commodity: 'electricity',
    rate_schedule: '',
    multiplier: 1,
    is_master_meter: false,
    pms_meter_code: '',
    bp_meter_id: '',
    status: 'active',
  });

  useEffect(() => {
    const initializePage = async () => {
      try {
        const { data: accounts } = await api.get('/auth/accounts');
        const storedAccountStr = localStorage.getItem('current_account');
        const storedAccount = storedAccountStr ? JSON.parse(storedAccountStr) : null;
        
        let account = null;
        if (storedAccount) {
          account = accounts.find(a => a.id === storedAccount.id);
        }
        if (!account && accounts.length > 0) {
          account = accounts[0];
        }
        
        if (!account) {
          navigate('/dashboard');
          toast.error('No account found');
          return;
        }
        
        localStorage.setItem('current_account', JSON.stringify(account));
        
        if (!account.enabled_apps?.ubm) {
          navigate('/dashboard');
          toast.error('UBM is not enabled for this organization');
          return;
        }
        
        setCurrentAccount(account);
        setInitializing(false);
      } catch (error) {
        console.error('Failed to initialize:', error);
        navigate('/dashboard');
        toast.error('Failed to load account data');
      }
    };
    
    initializePage();
  }, [navigate]);

  useEffect(() => {
    if (currentAccount && !initializing) {
      setPage(1);
      setMeters([]);
      setHasMore(true);
      fetchPage(currentAccount.id, 1);
      fetchStats(currentAccount.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, initializing]);

  // Filter changes -> reset
  useEffect(() => {
    if (currentAccount && !initializing) {
      setPage(1);
      setMeters([]);
      setHasMore(true);
      fetchPage(currentAccount.id, 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (currentAccount) {
        setPage(1);
        setMeters([]);
        setHasMore(true);
        fetchPage(currentAccount.id, 1);
      }
    }, 400);
  };

  const fetchStats = useCallback(async (accountId) => {
    try {
      const { data } = await api.get(`/ubm/accounts/${accountId}/meters/stats`);
      setMeterStats(data);
    } catch { /* non-critical */ }
  }, []);

  const fetchPage = useCallback(async (accountId, pageNum, append = false) => {
    if (!accountId) return;
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum);
      params.append('page_size', PAGE_SIZE);
      if (searchQuery) params.append('search', searchQuery);
      if (locationFilter !== 'all') params.append('location_id', locationFilter);
      if (commodityFilter !== 'all') params.append('commodity', commodityFilter);

      const [metersRes, ...rest] = await Promise.all([
        api.get(`/ubm/accounts/${accountId}/meters?${params.toString()}`),
        ...(pageNum === 1 ? [
          api.get(`/ubm/accounts/${accountId}/locations`),
          api.get(`/ubm/accounts/${accountId}/spaces?page_size=200`)
        ] : []),
      ]);
      const mData = metersRes.data;
      const newItems = mData.items || mData || [];
      
      if (append) {
        setMeters(prev => [...prev, ...newItems]);
      } else {
        setMeters(newItems);
      }
      setHasMore(pageNum < (mData.total_pages || 0));
      
      if (rest.length >= 2) {
        setLocations(rest[0].data || []);
        const spacesData = rest[1].data?.items || rest[1].data || [];
        setSpaces(spacesData);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load data:', error);
      }
      if (!append) { setMeters([]); setLocations([]); setSpaces([]); }
      setHasMore(false);
    }
    setLoading(false);
    setLoadingMore(false);
  }, [searchQuery, locationFilter, commodityFilter]);

  // Scroll-based lazy loading
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPage(currentAccount?.id, nextPage, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page, currentAccount]);

  // Reset page on commodity filter change (server-side)
  useEffect(() => {
    if (!currentAccount) return;
    setPage(1);
    setMeters([]);
    setHasMore(true);
    fetchPage(currentAccount.id, 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodityFilter]);

  const refreshAll = () => {
    if (!currentAccount) return;
    setPage(1);
    setMeters([]);
    setHasMore(true);
    fetchPage(currentAccount.id, 1);
    fetchStats(currentAccount.id);
  };

  const handleExportCSV = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/ubm/accounts/${currentAccount.id}/meters/export`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meters_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const resetForm = () => {
    setForm({
      location_id: '',
      space_id: '',
      meter_number: '',
      commodity: 'electricity',
      rate_schedule: '',
      multiplier: 1,
      is_master_meter: false,
      pms_meter_code: '',
      bp_meter_id: '',
      status: 'active',
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.location_id || !form.meter_number) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const submitData = { ...form };
      if (!submitData.space_id) delete submitData.space_id;
      await api.post(`/ubm/accounts/${currentAccount.id}/meters`, submitData);
      toast.success('Meter added successfully');
      setShowAddModal(false);
      resetForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add meter');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedMeter) return;
    
    setSaving(true);
    try {
      await api.put(`/ubm/accounts/${currentAccount.id}/meters/${selectedMeter.id}`, form);
      toast.success('Meter updated successfully');
      setShowEditModal(false);
      setSelectedMeter(null);
      resetForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update meter');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMeter) return;
    
    try {
      await api.delete(`/ubm/accounts/${currentAccount.id}/meters/${selectedMeter.id}`);
      toast.success('Meter deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedMeter(null);
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete meter');
    }
  };

  const openEditModal = (meter) => {
    setSelectedMeter(meter);
    setForm({
      location_id: meter.location_id || '',
      space_id: meter.space_id || '',
      meter_number: meter.meter_number || '',
      commodity: meter.commodity || 'electricity',
      rate_schedule: meter.rate_schedule || '',
      multiplier: meter.multiplier || 1,
      is_master_meter: meter.is_master_meter || false,
      pms_meter_code: meter.pms_meter_code || '',
      bp_meter_id: meter.bp_meter_id || '',
      status: meter.status || 'active',
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (meter) => {
    setSelectedMeter(meter);
    setShowDeleteConfirm(true);
  };

  // CSV Upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/ubm/accounts/${currentAccount.id}/meters/csv-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'meters_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`/ubm/accounts/${currentAccount.id}/meters/csv-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResults(data);
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Created ${data.created}, Updated ${data.updated} meters`);
        refreshAll();
      }
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
      setUploadResults({ errors: [{ error: error.response?.data?.detail || 'Upload failed' }] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // All filtering is server-side now
  const filteredMeters = meters;

  // Get spaces for selected location
  const filteredSpaces = spaces.filter(s => s.location_id === form.location_id);

  const commodities = [
    { value: 'electricity', label: 'Electricity', icon: Zap, color: 'text-yellow-600' },
    { value: 'gas', label: 'Gas', icon: Flame, color: 'text-orange-600' },
    { value: 'water', label: 'Water', icon: Droplet, color: 'text-blue-600' },
    { value: 'sewer', label: 'Sewer', icon: Droplet, color: 'text-purple-600' },
    { value: 'stormwater', label: 'Stormwater', icon: Droplet, color: 'text-teal-600' },
    { value: 'trash', label: 'Trash', icon: Wind, color: 'text-gray-700' },
    { value: 'ev_charger', label: 'EV Charger', icon: Car, color: 'text-green-600' },
    { value: 'solar', label: 'Solar', icon: Sun, color: 'text-amber-500' },
    { value: 'steam', label: 'Steam', icon: Wind, color: 'text-gray-600' },
    { value: 'chilled_water', label: 'Chilled Water', icon: Snowflake, color: 'text-cyan-600' },
    { value: 'internet', label: 'Internet', icon: Gauge, color: 'text-indigo-600' },
    { value: 'cable', label: 'Cable', icon: Gauge, color: 'text-red-600' },
    { value: 'phone', label: 'Phone', icon: Gauge, color: 'text-sky-600' },
  ];

  const getCommodityInfo = (commodity) => {
    const found = commodities.find(c => c.value === commodity);
    return found || { label: commodity, icon: Gauge, color: 'text-gray-600' };
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gauge className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Meters</h1>
              <p className="text-sm text-muted-foreground">
                Track utility meters for electricity, gas, water, and more
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="rounded-none"
              data-testid="export-csv-btn"
              disabled={meterStats.total === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(true)}
              className="rounded-none"
              data-testid="upload-csv-btn"
              disabled={locations.length === 0}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </Button>
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="rounded-none"
              data-testid="add-meter-btn"
              disabled={locations.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Meter
            </Button>
          </div>
        </div>

        {locations.length === 0 && !loading && (
          <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-none">
            <p className="text-sm text-yellow-800">
              <strong>No properties found.</strong> You need to create a property first before adding meters.
              <Button 
                variant="link" 
                className="p-0 h-auto ml-2 text-yellow-800 underline"
                onClick={() => navigate('/ubm/properties')}
              >
                Go to Properties
              </Button>
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {commodities.map(commodity => {
            const Icon = commodity.icon;
            const count = meterStats.by_commodity[commodity.value] || 0;
            return (
              <div key={commodity.value} className="border border-border rounded-none p-3 bg-card">
                <div className={`flex items-center gap-2 ${commodity.color} mb-1`}>
                  <Icon className="h-4 w-4" />
                  <span className="text-xs uppercase tracking-wide font-medium">{commodity.label}</span>
                </div>
                <p className="text-2xl font-black tracking-tighter">{count}</p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search meters..."
                className="rounded-none pl-9"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>
          <div className="w-40">
            <Select value={locationFilter} onValueChange={(v) => { setLocationFilter(v); }}>
              <SelectTrigger className="rounded-none">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={commodityFilter} onValueChange={setCommodityFilter}>
              <SelectTrigger className="rounded-none">
                <SelectValue placeholder="All Commodities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Commodities</SelectItem>
                {commodities.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={refreshAll}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Table */}
        <div className="border border-border rounded-none bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Meter</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Commodity</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Property</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Space</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">BP Meter ID</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Rate</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Type</th>
                  <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {initializing || loading ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      {initializing ? 'Initializing...' : 'Loading meters...'}
                    </td>
                  </tr>
                ) : filteredMeters.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-muted-foreground">
                      <Gauge className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No meters found</p>
                      <p className="text-xs mt-1">Add meters to track utility consumption</p>
                    </td>
                  </tr>
                ) : (
                  filteredMeters.map((meter) => {
                    const commodityInfo = getCommodityInfo(meter.commodity);
                    const CommodityIcon = commodityInfo.icon;
                    return (
                      <tr key={meter.id} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">
                          <div>
                            <p className="font-medium font-mono">{meter.meter_number}</p>
                            {meter.pms_meter_code && (
                              <p className="text-xs text-muted-foreground font-mono">{meter.pms_meter_code}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className={`flex items-center gap-2 ${commodityInfo.color}`}>
                            <CommodityIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">{commodityInfo.label}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{meter.location_name}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Box className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{meter.space_name || 'Building Level'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-xs font-mono text-muted-foreground">{meter.bp_meter_id || '-'}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{meter.rate_schedule || '-'}</span>
                        </td>
                        <td className="p-3">
                          {meter.is_master_meter ? (
                            <Badge className="rounded-none text-xs bg-purple-100 text-purple-800">
                              Master
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="rounded-none text-xs">
                              Sub
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-none"
                              onClick={() => openEditModal(meter)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                              onClick={() => openDeleteConfirm(meter)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Infinite scroll sentinel + count */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {filteredMeters.length} of {meterStats.total} meter(s)
          </p>
        </div>
        {loadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
            <span className="text-sm text-muted-foreground">Loading more...</span>
          </div>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>

      {/* Add Meter Side Panel */}
      <Sheet open={showAddModal} onOpenChange={setShowAddModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="add-meter-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Add Meter</SheetTitle>
            <SheetDescription>Add a new utility meter to track consumption.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleAdd} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Property *</Label>
                <Select value={form.location_id} onValueChange={(value) => setForm({ ...form, location_id: value, space_id: '' })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Select a location" /></SelectTrigger>
                  <SelectContent>{locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Space (optional - leave empty for building-level meter)</Label>
                <Select value={form.space_id || "__none__"} onValueChange={(value) => setForm({ ...form, space_id: value === "__none__" ? "" : value })} disabled={!form.location_id}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="Building Level (no space)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Building Level (no space)</SelectItem>
                    {filteredSpaces.map(space => (<SelectItem key={space.id} value={space.id}>{space.unit_number || space.space_type} - {space.space_type}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Meter Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.meter_number} onChange={(e) => setForm({ ...form, meter_number: e.target.value })} placeholder="e.g., E-12345" required />
              </div>
              <div>
                <Label>Commodity *</Label>
                <Select value={form.commodity} onValueChange={(value) => setForm({ ...form, commodity: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate Schedule</Label>
                <Input className="rounded-none mt-1" value={form.rate_schedule} onChange={(e) => setForm({ ...form, rate_schedule: e.target.value })} placeholder="e.g., TOU-D-PRIME" />
              </div>
              <div>
                <Label>Multiplier</Label>
                <Input className="rounded-none mt-1" type="number" step="0.01" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: parseFloat(e.target.value) || 1 })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-meter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="decommissioned">Decommissioned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PMS Meter Code</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.pms_meter_code} onChange={(e) => setForm({ ...form, pms_meter_code: e.target.value })} placeholder="e.g., MTR001" />
              </div>
              <div>
                <Label>BP Meter ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_meter_id} onChange={(e) => setForm({ ...form, bp_meter_id: e.target.value })} placeholder="BillPortal entity ID" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox id="is_master" checked={form.is_master_meter} onCheckedChange={(checked) => setForm({ ...form, is_master_meter: checked })} />
                <Label htmlFor="is_master" className="cursor-pointer">This is a master/house meter</Label>
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-add-meter-btn">{saving ? 'Adding...' : 'Add Meter'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Edit Meter Side Panel */}
      <Sheet open={showEditModal} onOpenChange={setShowEditModal}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="edit-meter-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Edit Meter</SheetTitle>
            <SheetDescription>Update meter details.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleEdit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meter Number *</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.meter_number} onChange={(e) => setForm({ ...form, meter_number: e.target.value })} required />
              </div>
              <div>
                <Label>Commodity *</Label>
                <Select value={form.commodity} onValueChange={(value) => setForm({ ...form, commodity: value })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{commodities.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate Schedule</Label>
                <Input className="rounded-none mt-1" value={form.rate_schedule} onChange={(e) => setForm({ ...form, rate_schedule: e.target.value })} />
              </div>
              <div>
                <Label>Multiplier</Label>
                <Input className="rounded-none mt-1" type="number" step="0.01" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: parseFloat(e.target.value) || 1 })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-meter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="decommissioned">Decommissioned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>PMS Meter Code</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.pms_meter_code} onChange={(e) => setForm({ ...form, pms_meter_code: e.target.value })} />
              </div>
              <div>
                <Label>BP Meter ID</Label>
                <Input className="rounded-none mt-1 font-mono" value={form.bp_meter_id} onChange={(e) => setForm({ ...form, bp_meter_id: e.target.value })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox id="is_master_edit" checked={form.is_master_meter} onCheckedChange={(checked) => setForm({ ...form, is_master_meter: checked })} />
                <Label htmlFor="is_master_edit" className="cursor-pointer">This is a master/house meter</Label>
              </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="rounded-none" disabled={saving} data-testid="save-edit-meter-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Meter</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete meter "{selectedMeter?.meter_number}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-none" onClick={handleDelete}>
              Delete Meter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { setShowUploadModal(open); if (!open) setUploadResults(null); }}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Upload Meters CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk create or update meters. Existing meters will be matched by PMS Meter Code or Meter Number.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="border border-dashed border-border rounded-none p-4 bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground">Get a CSV template with all required columns</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>

            {/* Upload Area */}
            <div className="border border-dashed border-border rounded-none p-6 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSV files only
                </p>
              </label>
            </div>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-2">
                {(uploadResults.created > 0 || uploadResults.updated > 0) && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded-none">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Created: {uploadResults.created}, Updated: {uploadResults.updated}</span>
                  </div>
                )}
                {uploadResults.errors?.length > 0 && (
                  <div className="bg-red-50 p-2 rounded-none">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{uploadResults.errors.length} Error(s)</span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {uploadResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-700">
                          {err.row ? `Row ${err.row}: ` : ''}{err.error}
                        </p>
                      ))}
                      {uploadResults.errors.length > 5 && (
                        <p className="text-xs text-red-500 mt-1">
                          ...and {uploadResults.errors.length - 5} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => { setShowUploadModal(false); setUploadResults(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Meters;
